import { Button } from "@/components/ui/button";
import { MoreVertical, Pencil, Trash2, Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import EditProjectModal from "./EditProjectModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Project {
  id: string;
  title: string;
  description: string | null;
  media_url: string | null;
  media_type: string | null;
}

interface ProjectCardProps {
  project: Project;
  isEditing: boolean;
  onClick: () => void;
  onUpdate?: () => void;
}

const ProjectCard = ({ project, isEditing, onClick, onUpdate }: ProjectCardProps) => {
  const { toast } = useToast();
  const [showEditModal, setShowEditModal] = useState(false);

  const handleEditProject = () => {
    setShowEditModal(true);
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      // Delete the media file if it exists
      if (project.media_url) {
        const filePath = project.media_url.split('/').pop();
        if (filePath) {
          await supabase.storage
            .from('portfolio')
            .remove([filePath]);
        }
      }

      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', project.id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Project deleted successfully",
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="relative group">
      <div 
        className="aspect-video rounded-lg overflow-hidden bg-muted cursor-pointer"
        onClick={onClick}
      >
        {project.media_url ? (
          project.media_type?.startsWith('video') ? (
            <div className="relative w-full h-full">
              <video
                src={project.media_url}
                className="w-full h-full object-cover"
                preload="metadata"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                <Play className="w-12 h-12 text-white" />
              </div>
            </div>
          ) : project.media_type === 'image' ? (
            <img
              src={project.media_url}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              Unsupported media type
            </div>
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            No media
          </div>
        )}
      </div>
      
      {isEditing && (
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white rounded-full"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleEditProject}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-destructive"
                onClick={handleDeleteProject}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      
      <div className="p-2">
        <h4 className="font-medium">{project.title}</h4>
        {project.description && (
          <p className="text-sm text-muted-foreground">{project.description}</p>
        )}
      </div>

      <EditProjectModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        project={project}
        onProjectUpdated={() => {
          if (onUpdate) onUpdate();
        }}
      />
    </div>
  );
};

export default ProjectCard;